import { Commit, ActionTree } from "vuex";
import { State } from "./applications.store";
import { AxiosResponse } from "axios";
import graphQl from "@/services/graphQl";
import graphHelpers from '@/utils/graphHelper';
import { Context, Params } from '@/utils/types/global';
import getEnv from '@/utils/env';
import { SM_FALLBACK_IP } from '@/utils/constants';

const actions: ActionTree<State, any> = {
  async GET_DASHBOARD_TABLE_DATA(
    context: { commit: Commit },
    params: {
      serviceId: string;
      clientIp: Array<string[]>;
      syntheticMetric: string;
      pagination: {
        pageNum: Number;
        pageSize: Number;
      };
      duration: {
        start: String;
        end: String;
        step: String;
      };
      order: string;
    }
  ): Promise<any> {
    if (!params) {
      return new Promise<void>((resolve) => resolve());
    }
    return graphQl
      .query("querySyntheticMetricTableValue")
      .params(params)
      .then((res: AxiosResponse) => {
        if (res.data) {
          return res.data.data.getSyntheticMetricTableValue;
        } else return [];
      });
  },
  async GET_SUMMARY_LIST_DATA(
    context: { commit: Commit },
    params: {
      serviceId: string;
      duration: {
        start: string;
        end: string;
        step: string;
      };
      summary: string;
      order: string;
    }
  ): Promise<any> {
    if (!params) {
      return new Promise<void>((resolve) => resolve());
    }
    return graphQl
      .query("getSummaryDataList")
      .params(params)
      .then((res: AxiosResponse) => {
        if (res.data) {
          return res.data.data.getSummaryTemplate;
        } else return [];
      });
  },
  async GET_SUMMARY_RECORD_DATA(
    context: { commit: Commit },
    params: {
      summaryId: string;
    }
  ): Promise<any> {
    if (!params) {
      return new Promise<void>((resolve) => resolve());
    }
    return graphQl
      .query("getSummaryInfo")
      .params(params)
      .then((res: AxiosResponse) => {
        if (res.data) {
          return res.data.data.getSummaryInfoTemplate;
        } else return {};
      });
  },
  async GET_DASHBOARD_CARD_DATA(
    context: { commit: Commit },
    params: {
      serviceId: string;
      clientIp: Array<string[]>;
      duration: {
        start: String;
        end: String;
        step: String;
      };
      syntheticMetricNamesList: Array<string[]>;
    }
  ): Promise<any> {
    if (!params) {
      return new Promise<void>((resolve) => resolve());
    }
    return graphQl
      .query("querySyntheticByDashboard")
      .params(params)
      .then((res: AxiosResponse) => {
        if (res.data) {
          return res.data.data.syntheticByDashboard.metricValue;
        } else return [];
      });
  },
  async GET_KPM_DATA(
    context: { commit: Commit },
    params: {
      keyword: string;
      serviceId: string;
      pagination: {
        pageNum: Number;
        pageSize: Number;
      };
    }
  ): Promise<any> {
    if (!params) {
      return new Promise<void>((resolve) => resolve());
    }
    return graphQl
      .query("queryGetKPMConfig")
      .params(params)
      .then((res: AxiosResponse) => {
        if (res.data) {
          return res.data.data.getKPMConfig;
        } else return [];
      });
  },
  async GET_AVAILABILITY(
    context: { commit: Commit },
    params: {
      serviceId: string;
      duration: {
        start: String;
        end: String;
        step: String;
      };
    }
  ): Promise<any> {
    if (!params) {
      return new Promise<void>((resolve) => resolve());
    }
    return graphQl
      .query("queryGetAvailability")
      .params(params)
      .then((res: AxiosResponse) => {
        if (res.data) {
          return res.data.data.getAvailability;
        } else return [];
      });
  },
  async GET_ENDPOINT_TABLE(
    context: { commit: Commit },
    params: {
      groupId: Number;
      searchString: string;
      urlAvailabilitySort: string;
      createdOn: string;
      sort: Object;
      serviceReq: Object;
      beginTime: Number;
      endTime: Number;
      offset: Number;
      limit: Number;
    }
  ): Promise<any> {
    return graphQl
      .query("getEndpointDetailsTable")
      .params(params)
      .then((res: AxiosResponse) => {
        if (res.data) {
          return res.data.data.getEndpointDetails;
        } else return {};
      });
  },
  async getTemplateMetric(
    context: { commit: Commit },
    params: { serviceId: any }
  ): Promise<any> {
    return graphQl
      .query('getSMTemplate')
      .params(params)
      .then(async (res: AxiosResponse) => {
        if (res.data) {
          context.commit('setTemplateMetrics', res.data.data.response);
        } else {
          context.commit('setTemplateMetrics', []);
        }
      });
  },
  getSMGraphData(context: Context, params: Params) {
    const normal = params.dashboardType.isDatabase ? false : true;
    let config = graphHelpers.createConfigSM(context, params);
    if (!config || !config.metricName || !config.queryMetricType) {
      return;
    }
    const { currentService } = context.rootState.syntheticState.commonState;
    const { currentEndpointId }  = context.rootState.syntheticState.endpoints;
    const metricNames = graphHelpers.parse(config.metricName);
    const labels = graphHelpers.parse(config.labelsIndex || '');
    const variablesList: Array<any> = metricNames.map((name: string) => {
      let variables = {} as any;
      variables = {
        duration: params.duration,
        condition: {
          name: config.metricName,
          entity: {
            scope: config.entityType,
            ...(config.entityType === 'Endpoint' ? currentEndpointId : '' ) && {endpointName: currentEndpointId},
            serviceName: currentService.key,
            synClientIP: getEnv('VUE_APP_SM_HOST')
              ? getEnv('VUE_APP_SM_HOST').split(',')
              : SM_FALLBACK_IP,
            normal
          }
        },
        labels
      };
      return variables;
    });
    if (!variablesList || !variablesList.length) {
      return;
    }

    return Promise.all(
      variablesList.map((variable: any) => {
        if (variable) {
          return graphQl
            .graphQuery(variable, config.queryMetricType)
            .then((res: AxiosResponse<any>) => {
              const resData = res.data.data;
              return {
                ...resData,
                config,
                metricName: variable.condition.name
              };
            });
        } else {
          return { config };
        }
      })
    ).then((data: any) => {
      return data;
    });
  },
  async SAVE_KPM(
    context: { commit: Commit },
    params: { KPMSyntheticRequest: any }
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      graphQl
        .query('postKPM')
        .params(params)
        .then(async (res: AxiosResponse<any>) => {
          if (!res.data.data) {
            throw Error(res.data.response.errorReason);
          }
          if (res.data.data.response && res.data.data.response.id) {
            resolve('1');
          } else {
            reject(res.data);
          }
        })
        .catch((err: any) => {
          console.log(err);
          reject(err);
        });
    });
  },
  async getErrorData(
    context: { commit: Commit; rootState: any },
    params: {
      serviceId: String;
      queryDuration: any;
      paging: any;
      sortKey: String;
      searchKeyword: String;
      clientIP: Array<any>;
    }
  ) {
    if (!params) {
      return new Promise<void>(resolve => resolve());
    }
    return graphQl
      .query('getSMErrors')
      .params(params)
      .then((res: AxiosResponse) => {
        if (res.data) {
          return res.data.data.queryBrowserErrorSummary;
        } else return [];
      });
  },
  async getErrorDetail(
    context: { commit: Commit; rootState: any },
    params: {
      serviceId: String;
      queryDuration: any;
      paging: any;
      serviceVersionId: String;
      pagePathId: String;
      category: String;
      sortKey: String;
      uniqueIds: Array<String>;
      userIds: Array<String>;
      browsers: Array<String>;
      devices: Array<String>;
      operatingSystems: Array<String>;
      message: String;
      searchKeyword: String;
      clientIP: Array<any>;
    }
  ) {
    if (!params) {
      return new Promise<void>(resolve => resolve());
    }
    return graphQl
      .query('getSMErrorLogs')
      .params(params)
      .then((res: AxiosResponse) => {
        if (res.data) {
          return res.data.data.queryBrowserErrorLogs;
        } else return [];
      });
  },
  async getEventDetail(
    context: { commit: Commit; rootState: any },
    params: {
      eventId: String;
    }
  ) {
    if (!params) {
      return new Promise<void>(resolve => resolve());
    }
    return graphQl
      .query('getSMErrorLog')
      .params(params)
      .then((res: AxiosResponse) => {
        if (res.data) {
          return res.data.data.queryBrowserErrorLog;
        } else return {};
      });
  },
  async GET_MAPPED_ENDPOINT_TABLE(
    context: { commit: Commit },
    params: any
  ): Promise<any> {
    return graphQl
      .query('getMappedEndpointDetailsTable')
      .params(params)
      .then((res: AxiosResponse) => {
        if (res.data) {
          return res.data.data.getEndpointDetails;
        } else return {};
      });
  },
};

export default actions;
